import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { useAuth, useChangePassword, useResetPassword } from "../../../core/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { Modal } from "antd";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_SPECIAL_REGEX = /[!@#$%^&*()_\-+=.[\]{}|;:'",<>?/\\`~]/;

const getPasswordStrengthError = (
  value: string,
  t: (key: string) => string,
  emptyKey = "auth.login.emptyFieldError"
): string | null => {
  if (value.trim() === "") {
    return t(emptyKey);
  }
  if (value.length < 8) {
    return t("auth.password.minLength");
  }
  if (!/[a-z]/.test(value)) {
    return t("auth.password.lowercase");
  }
  if (!/[A-Z]/.test(value)) {
    return t("auth.password.uppercase");
  }
  if (!/[0-9]/.test(value)) {
    return t("auth.password.number");
  }
  if (!PASSWORD_SPECIAL_REGEX.test(value)) {
    return t("auth.password.special");
  }
  return null;
};

const formatExpiresAtToHms = (value: string | number): string => {
  let totalSeconds: number;
  const numeric = typeof value === "number" ? value : Number(value);

  if (!Number.isNaN(numeric) && String(value).trim() !== "") {
    // Unix timestamp (giây) → thời gian còn lại
    totalSeconds = numeric > 1e9 ? Math.max(0, Math.floor(numeric - Date.now() / 1000)) : Math.max(0, Math.floor(numeric));
  } else {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    totalSeconds = Math.max(0, Math.floor((date.getTime() - Date.now()) / 1000));
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map((n) => String(n).padStart(2, "0")).join(":");
};

type PasswordField = "password" | "confirmPassword";
type FormView = "login" | "forgot";
type ForgotVerifyMode = "username" | "email";
type ForgotStep = "verify" | "receiveCode" | "resetPassword";

type ResetPasswordCache = {
  exists?: boolean;
  valid?: boolean;
  message?: string;
  user?: { expires_at?: string | number };
};

const initState: any = {
  username: "",
  password: "",
  service: "moodle_mobile_app",
};

const Login = () => {
  const loginSLider = {
    dots: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
  };
  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    confirmPassword: false,
  });
  const {
    checkUserExistByUsername,
    checkUserExistByEmail,
    isCheckingUser,
    resetPasswordData,
    clearResetPasswordData,
    requestForgotPasswordByUsername,
    requestForgotPasswordByEmail,
    isRequestingForgotPassword,
    checkValidToken,
    isCheckingValidToken,
    clearTokenValidation,
    confirmResetPassword,
    isConfirmingResetPassword,
  } = useResetPassword();
  const [loginData, setLoginData] = useState(initState);
  const [rememberMe, setRememberMe] = useState(false);
  const [formView, setFormView] = useState<FormView>("login");
  const [forgotVerifyMode, setForgotVerifyMode] = useState<ForgotVerifyMode>("username");
  const [forgotStep, setForgotStep] = useState<ForgotStep>("verify");
  const [forgotUsername, setForgotUsername] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotUsernameError, setForgotUsernameError] = useState<string | null>(null);
  const [forgotEmailError, setForgotEmailError] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationCodeError, setVerificationCodeError] = useState<string | null>(null);
  const [verificationNotCheckedError, setVerificationNotCheckedError] = useState<string | null>(
    null
  );
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  const [resetNewPasswordError, setResetNewPasswordError] = useState<string | null>(null);
  const [resetConfirmPasswordError, setResetConfirmPasswordError] = useState<string | null>(null);
  const [resetPasswordVisibility, setResetPasswordVisibility] = useState({
    new: false,
    confirm: false,
  });
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isForceChangePasswordVisible, setIsForceChangePasswordVisible] = useState(false);
  const [isChangePasswordModalVisible, setIsChangePasswordModalVisible] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changePasswordError, setChangePasswordError] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const { moodleLogin, isAuthLoading } = useAuth();
  const { t } = useTranslation();
  const { mutate: changePassword, isPending: isChangingPassword } = useChangePassword(() => {
    setIsChangePasswordModalVisible(false);
    setIsForceChangePasswordVisible(false);
  });

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prevState) => ({ ...prevState, [field]: !prevState[field] }));
  };
  const validateInput = (name: string, value: string) => {
    let error: string | null = null;
    if (value.trim() === "") {
      error = t("auth.login.emptyFieldError");
    } else if (name === "username" && !/^[a-zA-Z0-9_]*$/.test(value)) {
      error = t("auth.login.usernameSpecialCharError");
    }
    return error;
  };
  const validateForgotUsername = (value: string) => validateInput("username", value);
  const validateForgotEmail = (value: string) => {
    if (value.trim() === "") {
      return t("auth.login.emptyFieldError");
    }
    if (!EMAIL_REGEX.test(value.trim())) {
      return t("profile.emailInvalid");
    }
    return null;
  };
  const handleForgotSend = () => {
    if (forgotVerifyMode === "username") {
      const err = validateForgotUsername(forgotUsername);
      setForgotUsernameError(err);
      setForgotEmailError(null);
      if (err) return;
      checkUserExistByUsername(forgotUsername.trim());
    } else {
      const err = validateForgotEmail(forgotEmail);
      setForgotEmailError(err);
      setForgotUsernameError(null);
      if (err) return;
      checkUserExistByEmail(forgotEmail.trim());
    }
  };
  const clearForgotResetForm = () => {
    setVerificationCode("");
    setVerificationCodeError(null);
    setVerificationNotCheckedError(null);
    setResetNewPassword("");
    setResetConfirmPassword("");
    setResetNewPasswordError(null);
    setResetConfirmPasswordError(null);
  };
  const clearForgotErrors = () => {
    setForgotUsernameError(null);
    setForgotEmailError(null);
    clearResetPasswordData();
    clearForgotResetForm();
    setForgotStep("verify");
  };
  const resetPasswordCache = resetPasswordData as ResetPasswordCache | null | undefined;
  const isResetUserExists =
    resetPasswordCache != null && resetPasswordCache.exists === true;
  const hasTokenCheckResult =
    resetPasswordCache != null && typeof resetPasswordCache.valid === "boolean";
  const isTokenValid = resetPasswordCache?.valid === true;
  const tokenExpiresAt = resetPasswordCache?.user?.expires_at;
  const formattedTokenExpiresAt =
    tokenExpiresAt != null ? formatExpiresAtToHms(tokenExpiresAt) : null;
  const showReceiveCodeForm = forgotStep === "receiveCode";
  const showResetPasswordForm = forgotStep === "resetPassword";

  useEffect(() => {
    if (isResetUserExists) {
      setForgotStep("receiveCode");
    }
  }, [isResetUserExists]);
  const verifiedIdentifier =
    forgotVerifyMode === "username" ? forgotUsername : forgotEmail;
  const validateVerificationCode = (value: string) => {
    if (value.trim() === "") {
      return t("auth.login.emptyFieldError");
    }
    return null;
  };
  const validateResetNewPassword = (value: string) =>
    getPasswordStrengthError(value, t);
  const validateResetConfirmPassword = (confirm: string, newPwd: string) => {
    if (confirm.trim() === "") {
      return t("auth.login.emptyFieldError");
    }
    if (confirm !== newPwd) {
      return t("auth.changePassword.error.passwordMismatch");
    }
    return null;
  };
  const handleForgotContinue = () => {
    const value = verifiedIdentifier.trim();
    const goToResetForm = () => {
      clearForgotResetForm();
      setForgotStep("resetPassword");
    };
    if (forgotVerifyMode === "username") {
      requestForgotPasswordByUsername(value, { onSuccess: goToResetForm });
    } else {
      requestForgotPasswordByEmail(value, { onSuccess: goToResetForm });
    }
  };
  const handleCheckVerificationCode = () => {
    const err = validateVerificationCode(verificationCode);
    setVerificationCodeError(err);
    setVerificationNotCheckedError(null);
    clearTokenValidation();
    if (err) return;

    checkValidToken(verificationCode.trim());
  };
  const handleResetPasswordConfirm = () => {
    const codeErr = validateVerificationCode(verificationCode);
    const newPwdErr = validateResetNewPassword(resetNewPassword);
    const confirmErr = validateResetConfirmPassword(resetConfirmPassword, resetNewPassword);

    setVerificationCodeError(codeErr);
    setResetNewPasswordError(newPwdErr);
    setResetConfirmPasswordError(confirmErr);

    if (!isTokenValid) {
      setVerificationNotCheckedError("Vui lòng kiểm tra mã xác minh trước khi xác nhận.");
      return;
    }
    setVerificationNotCheckedError(null);

    if (codeErr || newPwdErr || confirmErr) {
      return;
    }

    confirmResetPassword(
      { token: verificationCode.trim(), password: resetNewPassword },
      {
        onSuccess: () => {
          clearForgotErrors();
          setFormView("login");
        },
      }
    );
  };
  const toggleResetPasswordVisibility = (field: "new" | "confirm") => {
    setResetPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const usernameErr = validateInput("username", loginData.username);
    const passwordErr = validateInput("password", loginData.password);
    setUsernameError(usernameErr);
    setPasswordError(passwordErr);
    if (usernameErr || passwordErr) {
      return;
    }
    if (rememberMe) {
      localStorage.setItem("rememberMe", JSON.stringify({ username: loginData.username }));
    } else {
      localStorage.removeItem("rememberMe");
    }
    try {
      moodleLogin(loginData);
    } catch (err) {
      // Xử lý lỗi nếu cần
    }
  };
  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setLoginData((prevState: any) => ({ ...prevState, [name]: value }));

    if (name === "username") {
      setUsernameError(validateInput(name, value));
    } else if (name === "password") {
      setPasswordError(validateInput(name, value));
    }
  };
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === "username") {
      setUsernameError(validateInput(name, value));
    } else if (name === "password") {
      setPasswordError(validateInput(name, value));
    }
  };
  const onHandleRememberMe = (event: any) => {
    const { checked } = event.target;
    setRememberMe(checked);
  };
  useEffect(() => {
    const savedLoginData = localStorage.getItem("rememberMe");
    if (savedLoginData) {
      const { username } = JSON.parse(savedLoginData);
      setLoginData((prevData: any) => ({ ...prevData, username }));
      setRememberMe(true);
    }
  }, []);

  // 👇 Lắng nghe sự kiện FORCE_PASSWORD_CHANGE được phát ra từ useAuth
  useEffect(() => {
    const handler = () => {
      setIsForceChangePasswordVisible(true);
    };
    window.addEventListener("FORCE_PASSWORD_CHANGE", handler);
    return () => {
      window.removeEventListener("FORCE_PASSWORD_CHANGE", handler);
    };
  }, []);

  return (
    <>
      <div className="main-wrapper">
        <div className="login-content">
          <div className="row flex-grow-1 d-flex justify-content-center">
            <div className="col-lg-6 login-bg d-none d-lg-flex p-0">
              <ImageWithBasePath
                src="assets/img/auth/auth-2.png"
                className="img-fluid w-100 h-100 object-fit-cover"
                alt="Logo"
              />
            </div>
            <div className="col-md-6 login-wrap-bg">
              <div className="login-wrapper align-content-center">
                <div className="loginbox">
                  <div className="w-70">
                    <div className="d-flex flex-column align-items-center justify-content-center gap-3 mb-5 login-header">
                      <ImageWithBasePath
                        src="assets/img/logo_cyber.png"
                        className="img-fluid"
                        alt="Logo"
                        width={600}
                        height={88}
                      />
                      <div>
                      <span className="text-cyber text-28">Hệ thống học tập trực tuyến LMS Cyber Uni</span>
                      </div>
                    </div>
                    <p className="text-secondary fw-bold d-flex justify-content-center mt-4 text-center system-name">
                      {formView === "login"
                        ? "ĐĂNG NHẬP"
                        : showResetPasswordForm
                          ? "ĐỔI MẬT KHẨU"
                          : showReceiveCodeForm
                            ? "NHẬN MÃ QUA EMAIL"
                            : "QUÊN MẬT KHẨU"}
                    </p>
                    <div className="d-flex justify-content-center w-100 w-md-75 mx-auto">
                      {formView === "login" ? (
                        <form
                          onSubmit={handleSubmit}
                          className="mb-3 pb-3 w-100 w-md-75"
                          autoComplete="off"
                        >
                          <div className="mb-3 position-relative">
                            <label className="form-label">
                              {t("auth.login.usernameLabel")}
                              <span className="text-danger ms-1">*</span>
                            </label>
                            <div className="position-relative">
                              <input
                                name="username"
                                value={loginData?.username || ""}
                                onChange={handleChange}
                                type="text"
                                autoComplete="off"
                                className="form-control form-control-lg"
                                onBlur={handleBlur}
                                readOnly={true}
                                onFocus={() => (document.querySelector('input[name="username"]') as HTMLInputElement).removeAttribute('readonly')}
                              />
                              <span>
                                <i className="isax isax-sms input-icon text-gray-7 fs-14" />
                              </span>
                              {usernameError && (
                                <div className="invalid-feedback d-block">{usernameError}</div>
                              )}
                            </div>
                          </div>
                          <div className="mb-3 position-relative">
                            <label className="form-label">
                              {t("auth.login.passwordLabel")}
                              <span className="text-danger ms-1">*</span>
                            </label>
                            <div className="position-relative" id="passwordInput">
                              <input
                                name="password"
                                value={loginData?.password || ""}
                                onChange={handleChange}
                                type={passwordVisibility.password ? "text" : "password"}
                                autoComplete="off"
                                className="form-control form-control-lg pass-input"
                                onBlur={handleBlur}
                                readOnly={true}
                                onFocus={() => (document.querySelector('input[name="password"]') as HTMLInputElement).removeAttribute('readonly')}
                              />
                              <span
                                className={`isax toggle-passwords fs-14 ${
                                  passwordVisibility.password ? "isax-eye" : "isax-eye-slash"
                                }`}
                                onClick={() => togglePasswordVisibility("password")}
                              />
                              {passwordError && (
                                <div className="invalid-feedback d-block">{passwordError}</div>
                              )}
                            </div>
                          </div>
                          <div className="d-flex align-items-center justify-content-between mb-4">
                            <div className="remember-me d-flex align-items-center">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="flexCheckDefault"
                                checked={rememberMe}
                                onChange={onHandleRememberMe}
                              />
                              <label className="form-check-label ms-2" htmlFor="flexCheckDefault">
                                {t("auth.login.rememberMe")}
                              </label>
                            </div>
                            <button
                              type="button"
                              className="btn btn-link p-0 text-decoration-none"
                              onClick={() => {
                                setFormView("forgot");
                                setForgotVerifyMode("username");
                                clearForgotErrors();
                              }}
                            >
                              Quên mật khẩu
                            </button>
                          </div>
                          <div className="d-grid">
                            <button
                              className="btn btn-cyberuni btn-lg"
                              type="submit"
                              disabled={isAuthLoading}
                            >
                              {t("auth.login.loginButton")}
                              <i className="isax isax-arrow-right-3 ms-1" />
                            </button>
                          </div>
                        </form>
                      ) : showResetPasswordForm ? (
                        <form
                          className="mb-3 pb-3 w-100 w-md-75"
                          autoComplete="off"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <p className="text-secondary mb-4">
                            Mã xác minh đã được gửi về email. Vui lòng mở email của bạn và tìm thư
                            chứa mã xác minh
                          </p>
                          <div className="mb-3 position-relative">
                            <label className="form-label" htmlFor="reset-verified-identifier">
                              {forgotVerifyMode === "username" ? "Username" : "Email"}
                            </label>
                            <input
                              id="reset-verified-identifier"
                              type={forgotVerifyMode === "username" ? "text" : "email"}
                              value={verifiedIdentifier}
                              readOnly
                              className="form-control form-control-lg bg-light"
                              autoComplete="off"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label" htmlFor="verification-code">
                              Mã xác minh
                            </label>
                            <div className="d-flex gap-2">
                              <input
                                id="verification-code"
                                type="text"
                                value={verificationCode}
                                onChange={(e) => {
                                  setVerificationCode(e.target.value);
                                  setVerificationCodeError(validateVerificationCode(e.target.value));
                                  clearTokenValidation();
                                  setVerificationNotCheckedError(null);
                                }}
                                className="form-control form-control-lg"
                                autoComplete="off"
                              />
                              <button
                                type="button"
                                className="btn btn-outline-secondary text-nowrap"
                                onClick={handleCheckVerificationCode}
                                disabled={isCheckingValidToken}
                              >
                                Kiểm tra
                              </button>
                            </div>
                            {verificationCodeError && (
                              <div className="invalid-feedback d-block">{verificationCodeError}</div>
                            )}
                            {hasTokenCheckResult && isTokenValid && !verificationCodeError && (
                              <div className="small mt-1">
                                Mã xác minh hợp lệ
                                {formattedTokenExpiresAt != null && (
                                  <> — Hết hạn trong {formattedTokenExpiresAt}</>
                                )}
                              </div>
                            )}
                            {hasTokenCheckResult && !isTokenValid && (
                              <div className="invalid-feedback d-block">
                                Mã xác minh không hợp lệ
                              </div>
                            )}
                          </div>
                          <div className="mb-3 position-relative">
                            <label className="form-label" htmlFor="reset-new-password">
                              {t("auth.changePassword.newPasswordLabel")}
                            </label>
                            <div className="position-relative" id="resetNewPasswordInput">
                              <input
                                id="reset-new-password"
                                type={resetPasswordVisibility.new ? "text" : "password"}
                                value={resetNewPassword}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setResetNewPassword(value);
                                  setResetNewPasswordError(validateResetNewPassword(value));
                                  if (resetConfirmPassword) {
                                    setResetConfirmPasswordError(
                                      validateResetConfirmPassword(resetConfirmPassword, value)
                                    );
                                  }
                                }}
                                onBlur={(e) =>
                                  setResetNewPasswordError(validateResetNewPassword(e.target.value))
                                }
                                className="form-control form-control-lg pass-input"
                                autoComplete="off"
                              />
                              <span
                                className={`isax toggle-passwords fs-14 ${
                                  resetPasswordVisibility.new ? "isax-eye" : "isax-eye-slash"
                                }`}
                                onClick={() => toggleResetPasswordVisibility("new")}
                              />
                              {resetNewPasswordError && (
                                <div className="invalid-feedback d-block">{resetNewPasswordError}</div>
                              )}
                            </div>
                          </div>
                          <div className="mb-3 position-relative">
                            <label className="form-label" htmlFor="reset-confirm-password">
                              {t("auth.changePassword.confirmPasswordLabel")}
                            </label>
                            <div className="position-relative" id="resetConfirmPasswordInput">
                              <input
                                id="reset-confirm-password"
                                type={resetPasswordVisibility.confirm ? "text" : "password"}
                                value={resetConfirmPassword}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setResetConfirmPassword(value);
                                  setResetConfirmPasswordError(
                                    validateResetConfirmPassword(value, resetNewPassword)
                                  );
                                }}
                                onBlur={(e) =>
                                  setResetConfirmPasswordError(
                                    validateResetConfirmPassword(e.target.value, resetNewPassword)
                                  )
                                }
                                className="form-control form-control-lg pass-input"
                                autoComplete="off"
                              />
                              <span
                                className={`isax toggle-passwords fs-14 ${
                                  resetPasswordVisibility.confirm ? "isax-eye" : "isax-eye-slash"
                                }`}
                                onClick={() => toggleResetPasswordVisibility("confirm")}
                              />
                              {resetConfirmPasswordError && (
                                <div className="invalid-feedback d-block">
                                  {resetConfirmPasswordError}
                                </div>
                              )}
                            </div>
                          </div>
                          {verificationNotCheckedError && (
                            <div className="invalid-feedback d-block mb-3">
                              {verificationNotCheckedError}
                            </div>
                          )}
                          <div className="d-grid mb-3">
                            <button
                              type="button"
                              className="btn btn-cyberuni btn-lg"
                              onClick={handleResetPasswordConfirm}
                              disabled={isConfirmingResetPassword}
                            >
                              Xác nhận
                            </button>
                          </div>
                          <div className="text-center">
                            <button
                              type="button"
                              className="btn btn-link p-0 text-decoration-none"
                              onClick={() => {
                                clearForgotResetForm();
                                setForgotStep("receiveCode");
                              }}
                            >
                              Quay lại
                            </button>
                          </div>
                        </form>
                      ) : showReceiveCodeForm ? (
                        <form
                          className="mb-3 pb-3 w-100 w-md-75"
                          autoComplete="off"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <div className="mb-3 position-relative">
                            <label className="form-label" htmlFor="forgot-verified-identifier">
                              {forgotVerifyMode === "username" ? "Username" : "Email"}
                            </label>
                            <input
                              id="forgot-verified-identifier"
                              type={forgotVerifyMode === "username" ? "text" : "email"}
                              value={verifiedIdentifier}
                              readOnly
                              className="form-control form-control-lg bg-light"
                              autoComplete="off"
                            />
                          </div>
                          <div className="d-grid mb-3">
                            <button
                              type="button"
                              className="btn btn-cyberuni btn-lg"
                              onClick={handleForgotContinue}
                              disabled={isRequestingForgotPassword}
                            >
                              Tiếp tục
                            </button>
                          </div>
                          <div className="text-center">
                            <button
                              type="button"
                              className="btn btn-link p-0 text-decoration-none"
                              onClick={() => {
                                clearResetPasswordData();
                                setForgotStep("verify");
                              }}
                            >
                              Quay lại
                            </button>
                          </div>
                        </form>
                      ) : (
                        <form
                          className="mb-3 pb-3 w-100 w-md-75"
                          autoComplete="off"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          {forgotVerifyMode === "username" ? (
                            <div className="mb-3 position-relative">
                              <label className="form-label" htmlFor="forgot-username">
                                Username
                              </label>
                              <input
                                id="forgot-username"
                                name="forgotUsername"
                                type="text"
                                value={forgotUsername}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setForgotUsername(value);
                                  setForgotUsernameError(validateForgotUsername(value));
                                  clearResetPasswordData();
                                }}
                                onBlur={(e) =>
                                  setForgotUsernameError(validateForgotUsername(e.target.value))
                                }
                                className="form-control form-control-lg"
                                autoComplete="off"
                              />
                              {forgotUsernameError && (
                                <div className="invalid-feedback d-block">{forgotUsernameError}</div>
                              )}
                              {resetPasswordData != null &&
                                (resetPasswordData as { exists?: boolean }).exists === false && (
                                <div className="invalid-feedback d-block">
                                  Tài khoản không tồn tại
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="mb-3 position-relative">
                              <label className="form-label" htmlFor="forgot-email">
                                Email
                              </label>
                              <input
                                id="forgot-email"
                                name="forgotEmail"
                                type="email"
                                value={forgotEmail}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setForgotEmail(value);
                                  setForgotEmailError(validateForgotEmail(value));
                                  clearResetPasswordData();
                                }}
                                onBlur={(e) =>
                                  setForgotEmailError(validateForgotEmail(e.target.value))
                                }
                                className="form-control form-control-lg"
                                autoComplete="off"
                              />
                              {forgotEmailError && (
                                <div className="invalid-feedback d-block">{forgotEmailError}</div>
                              )}
                              {resetPasswordData != null &&
                                (resetPasswordData as { exists?: boolean }).exists === false && (
                                <div className="invalid-feedback d-block">
                                  Tài khoản không tồn tại
                                </div>
                              )}
                            </div>
                          )}
                          <div className="d-grid mb-3">
                            <button
                              type="button"
                              className="btn btn-cyberuni btn-lg"
                              onClick={handleForgotSend}
                              disabled={isCheckingUser}
                            >
                              Gửi đi
                            </button>
                          </div>
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <button
                              type="button"
                              className="btn btn-link flex-fill text-decoration-none"
                              onClick={() => {
                                setForgotVerifyMode(
                                  forgotVerifyMode === "username" ? "email" : "username"
                                );
                                clearForgotErrors();
                              }}
                            >
                              {forgotVerifyMode === "username"
                                ? "Kiểm tra bằng email"
                                : "Kiểm tra bằng username"}
                            </button>
                            <button
                              type="button"
                              className="btn btn-link flex-fill text-decoration-none"
                              onClick={() => {
                                setFormView("login");
                                clearForgotErrors();
                              }}
                            >
                              Quay lại đăng nhập
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
                <div className="d-flex col-md-6 justify-content-center align-items-center login-form-footer gap-3 flex-wrap">
                  <span className="me-2">
                    <i className="fas fa-phone-alt" /> {t("footer.company.phone")}
                  </span>
                  <span className="me-2">
                    <i className="fas fa-envelope" /> {t("footer.company.email")}
                  </span>
                  <span>
                    <i className="fas fa-location" /> {t("footer.company.address")}
                  </span>
                </div>
              </div>
            </div>
            {/* /Login Banner */}
          </div>
        </div>
      </div>
      {/* Modal bắt buộc đổi mật khẩu */}
      <Modal
        footer={null}
        centered
        title={t("common.forceChangePasswordTitle") || "Đổi mật khẩu bắt buộc"}
        open={isForceChangePasswordVisible}
        onCancel={() => setIsForceChangePasswordVisible(false)}
      >
        <p>
          {t("common.forceChangePasswordMessage") ||
            "Bạn cần đổi mật khẩu trước khi sử dụng hệ thống. Vui lòng đổi mật khẩu để tiếp tục."}
        </p>
        <div style={{ textAlign: "right" }}>
          <a
            className="btn btn-primary"
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => {
              e.preventDefault();
              setIsForceChangePasswordVisible(false);
              setIsChangePasswordModalVisible(true);
            }}
          >
            {t("common.goToChangePassword") || "Đổi mật khẩu ngay"}
          </a>
        </div>
      </Modal>

      {/* Modal đổi mật khẩu nội bộ */}
      <Modal
        open={isChangePasswordModalVisible}
        onCancel={() => setIsChangePasswordModalVisible(false)}
        title={t("auth.changePassword.title")}
        footer={null}
        centered
      >
        <form
          onSubmit={e => {
            e.preventDefault();
            setChangePasswordError("");
            if (!oldPassword || !newPassword || !confirmPassword) {
              setChangePasswordError(t("auth.changePassword.error.fillAllFields"));
              return;
            }
            if (newPassword !== confirmPassword) {
              setChangePasswordError(t("auth.changePassword.error.passwordMismatch"));
              return;
            }
            const wstoken = sessionStorage.getItem("wstoken") || '';
            changePassword({ wstoken, oldpassword: oldPassword, newpassword: newPassword });
          }}
        >
          <div className="mb-3 position-relative">
            <label className="form-label">{t("auth.changePassword.oldPasswordLabel")}</label>
            <div className="position-relative">
              <input
                type={showPassword.old ? "text" : "password"}
                className="form-control"
                value={oldPassword}
                onChange={e => setOldPassword(e.target.value)}
              />
              <span
                className={`isax toggle-passwords fs-14 ${showPassword.old ? "isax-eye" : "isax-eye-slash"}`}
                style={{ position: 'absolute', top: '20px', right: '12px', cursor: 'pointer' }}
                onClick={() => setShowPassword(prev => ({ ...prev, old: !prev.old }))}
              />
            </div>
          </div>
          <div className="mb-3 position-relative">
            <label className="form-label">{t("auth.changePassword.newPasswordLabel")}</label>
            <div className="position-relative">
              <input
                type={showPassword.new ? "text" : "password"}
                className="form-control"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
              />
              <span
                className={`isax toggle-passwords fs-14 ${showPassword.new ? "isax-eye" : "isax-eye-slash"}`}
                style={{ position: 'absolute', top: '20px', right: '12px', cursor: 'pointer' }}
                onClick={() => setShowPassword(prev => ({ ...prev, new: !prev.new }))}
              />
            </div>
          </div>
          <div className="mb-3 position-relative">
            <label className="form-label">{t("auth.changePassword.confirmPasswordLabel")}</label>
            <div className="position-relative">
              <input
                type={showPassword.confirm ? "text" : "password"}
                className="form-control"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
              />
              <span
                className={`isax toggle-passwords fs-14 ${showPassword.confirm ? "isax-eye" : "isax-eye-slash"}`}
                style={{ position: 'absolute', top: '20px', right: '12px', cursor: 'pointer' }}
                onClick={() => setShowPassword(prev => ({ ...prev, confirm: !prev.confirm }))}
              />
            </div>
          </div>
          {changePasswordError && (
            <div className="text-danger mb-2">{changePasswordError}</div>
          )}
          <div className="d-flex justify-content-end">
            <button type="submit" className="btn btn-primary" disabled={isChangingPassword}>
              {isChangingPassword ? t("auth.changePassword.changing") : t("auth.changePassword.confirmButton")}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default Login;

